// @ts-nocheck
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogHeader, 
  DialogTitle, 
  DialogTrigger 
} from '@/components/ui/dialog';
import { 
  Users, 
  GraduationCap, 
  Trophy, 
  UserPlus, 
  Crown, 
  ArrowRight,
  CheckCircle 
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';

interface MembershipOption {
  slug: string;
  name: string;
  role_slug: string;
  description: string;
  icon: React.ComponentType<any>;
  features: string[];
  color: string;
}

const MembershipUpgradeAlert: React.FC = () => {
  const { profile, refreshProfile } = useAuth();
  const [upgradeDialogOpen, setUpgradeDialogOpen] = useState(false);
  const [upgrading, setUpgrading] = useState(false);
  const [selectedMembership, setSelectedMembership] = useState<string | null>(null);

  // Available upgrade options for customers
  const membershipOptions: MembershipOption[] = [
    {
      slug: 'athlete_free',
      name: 'Athlete',
      role_slug: 'athlete',
      description: 'Create your athletic profile, track recruiting, connect with coaches',
      icon: Trophy,
      features: [
        'Create detailed athletic profile',
        'Upload stats, videos, and achievements',
        'Track recruiting progress',
        'Connect with college coaches',
        'Access to recruiting timeline tools'
      ],
      color: 'text-blue-600'
    },
    {
      slug: 'parent_free',
      name: 'Parent',
      role_slug: 'parent',
      description: 'Support your athlete\'s recruiting journey with parent tools',
      icon: Users,
      features: [
        'View and manage your athlete\'s profile',
        'Track recruiting communications',
        'Access parent resources and guides',
        'Connect with other parents',
        'Monitor recruiting progress'
      ],
      color: 'text-green-600'
    },
    {
      slug: 'club_coach',
      name: 'Club Coach',
      role_slug: 'club_coach',
      description: 'Manage club teams, create rosters, and track player development',
      icon: UserPlus,
      features: [
        'Create and manage team rosters',
        'Invite players to your teams',
        'Track player development',
        'Access coaching resources',
        'Team communication tools',
        'Event management for your teams'
      ],
      color: 'text-purple-600'
    }
  ];

  // Check if user should see the upgrade alert (customer role/membership only)
  const shouldShowAlert = profile?.active_membership_slug === 'customer';

  const handleUpgrade = async (membershipSlug: string) => {
    if (!profile?.id) {
      toast.error('Profile not found');
      return;
    }

    setUpgrading(true);
    setSelectedMembership(membershipSlug);

    try {
      // Find the selected membership
      const selectedOption = membershipOptions.find(opt => opt.slug === membershipSlug);
      if (!selectedOption) {
        throw new Error('Invalid membership selection');
      }

      // First, deactivate current customer membership
      const { error: deactivateError } = await supabase
        .from('profile_memberships')
        .update({ is_active: false, updated_at: new Date().toISOString() })
        .eq('profile_id', profile.id)
        .eq('membership_slug', 'customer');

      if (deactivateError) {
        console.error('Error deactivating customer membership:', deactivateError);
        throw deactivateError;
      }

      // Create or update membership assignment using upsert
      const { error: assignError } = await supabase
        .from('profile_memberships')
        .upsert({
          profile_id: profile.id,
          membership_slug: membershipSlug,
          is_active: true,
          assigned_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'profile_id,membership_slug',
          ignoreDuplicates: false
        });

      if (assignError) {
        console.error('Error assigning new membership:', assignError);
        throw assignError;
      }

      // Roles live in user_roles and are server-owned. The client never writes
      // profiles.role — the database trigger preserves it regardless.


      toast.success(`Successfully upgraded to ${selectedOption.name}! Refreshing your dashboard...`);
      
      // Refresh the profile to get updated role
      await refreshProfile();
      
      // Close dialog
      setUpgradeDialogOpen(false);
      
      // Redirect to appropriate dashboard after a brief delay
      setTimeout(() => {
        const dashboardPath = getDashboardPath(selectedOption.role_slug);
        window.location.href = dashboardPath;
      }, 2000);

    } catch (error) {
      console.error('Membership upgrade error:', error);
      toast.error('Failed to upgrade membership. Please try again.');
    } finally {
      setUpgrading(false);
      setSelectedMembership(null);
    }
  };

  const getDashboardPath = (roleSlug: string): string => {
    switch (roleSlug) {
      case 'athlete':
        return '/athlete-dashboard';
      case 'parent':
        return '/parent-dashboard';
      case 'club_coach':
        return '/club-coach-dashboard';
      default:
        return '/customer-dashboard';
    }
  };

  if (!shouldShowAlert) {
    return null;
  }

  return (
    <div className="mb-6 p-6 rounded-lg border-2 border-amber-300 bg-gradient-to-r from-amber-50 to-orange-50 shadow-lg animate-pulse">
      <div className="flex items-start gap-4">
        <div className="flex-shrink-0">
          <Crown className="h-8 w-8 text-amber-600" />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <h3 className="text-xl font-bold text-amber-800">
              🚀 Unlock Your Full Potential!
            </h3>
            <Badge variant="secondary" className="bg-red-100 text-red-700 font-semibold animate-bounce">
              FREE UPGRADE
            </Badge>
          </div>
          <div className="space-y-3">
            <p className="text-amber-700 font-medium">
              You registered as a Customer. Upgrade to access powerful tools designed for your role:
            </p>
            <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
              <p className="text-blue-800 font-semibold text-sm flex items-center gap-2">
                <UserPlus className="h-4 w-4" />
                <strong>COACHES:</strong> This is the ONLY way to manage team rosters, invite players, and access coaching tools!
              </p>
            </div>
            <p className="text-amber-600 text-sm">
              All account types are completely FREE and give you access to specialized features!
            </p>
          </div>
        </div>
        <div className="flex-shrink-0">
          <Dialog open={upgradeDialogOpen} onOpenChange={setUpgradeDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-amber-600 hover:bg-amber-700 text-white font-semibold px-6 py-3 text-lg shadow-lg">
                Upgrade Account
                <ArrowRight className="h-5 w-5 ml-2" />
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-2xl">Choose Your Account Type</DialogTitle>
                <DialogDescription>
                  Select the account type that best matches your role. All upgrades are completely free!
                </DialogDescription>
              </DialogHeader>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                {membershipOptions.map((option) => {
                  const Icon = option.icon;
                  const isSelected = selectedMembership === option.slug;
                  const isUpgrading = upgrading && isSelected;
                  
                  return (
                    <Card 
                      key={option.slug} 
                      className={`relative cursor-pointer transition-all hover:shadow-md ${
                        isSelected ? 'ring-2 ring-primary' : ''
                      }`}
                    >
                      <CardHeader className="pb-3">
                        <div className="flex items-center gap-3">
                          <Icon className={`h-6 w-6 ${option.color}`} />
                          <div className="flex-1">
                            <CardTitle className="text-lg">{option.name}</CardTitle>
                            <Badge variant="secondary" className="text-xs">
                              {option.role_slug}
                            </Badge>
                          </div>
                        </div>
                        <p className="text-sm text-muted-foreground mt-2">
                          {option.description}
                        </p>
                      </CardHeader>
                      <CardContent className="pt-0">
                        <div className="space-y-2 mb-4">
                          {option.features.map((feature, index) => (
                            <div key={index} className="flex items-center gap-2">
                              <CheckCircle className="h-3 w-3 text-green-500 flex-shrink-0" />
                              <span className="text-xs text-muted-foreground">{feature}</span>
                            </div>
                          ))}
                        </div>
                        <Button
                          onClick={() => handleUpgrade(option.slug)}
                          disabled={upgrading}
                          className="w-full"
                          variant={isSelected ? "default" : "outline"}
                        >
                          {isUpgrading ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                              Upgrading...
                            </>
                          ) : (
                            `Upgrade to ${option.name}`
                          )}
                        </Button>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
              
              <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                <p className="text-sm text-blue-800 font-medium mb-1">
                  ✨ All account types are completely FREE!
                </p>
                <p className="text-xs text-blue-700">
                  You can always change your account type later if your needs change.
                </p>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>
    </div>
  );
};

export default MembershipUpgradeAlert;